import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getColor } from "@monorepo/charts";
import { getHistoryEntryPaidDate, padMonth } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import { IHistoryEntry } from "@monorepo/types";
import { keyframes, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { format } from "date-fns";

export const scaleY = keyframes`
0% {
  transform: scaleY(0.2);
}
100% {
  transform: scaleY(1);
}`;

type TPeriodDataType = Record<string, Record<number, number>>;

interface IProps {
  entries: Array<IHistoryEntry>;
  barsToShow: number;
  initialPeriod: string;
  onPeriodClick?: (period: string) => void;
  totalPeriodSum: number;
}
interface IArrowProps {
  up?: boolean;
}

const Arrow: React.FC<IArrowProps> = ({ up = false }) => (
  <Box
    component="span"
    sx={{
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      marginLeft: "0.25rem",
      ...(up && {
        borderBottom: `6px solid ${up ? theme.palette.error.dark : theme.palette.primary.main}`,
      }),
      ...(!up && {
        borderTop: `6px solid ${up ? theme.palette.error.dark : theme.palette.primary.main}`,
      }),
    }}
  />
);

export const HistoryBarChart: FC<IProps> = memo(
  ({ entries, initialPeriod, barsToShow, onPeriodClick, totalPeriodSum }) => {
    const { t } = useTranslation();

    const [selectedPeriod, setSelectedPeriod] = useState<string>(initialPeriod || format(new Date(), "yyyy-MM"));
    const [selectedYear, selectedMonth] = selectedPeriod.split("-").map(Number);
    const previousSelection = useRef<{ fromIndex: number; toIndex: number; selectedIndex: number }>();

    useEffect(() => {
      setSelectedPeriod(initialPeriod || format(new Date(), "yyyy-MM"));
    }, [initialPeriod]);

    const chartData = useMemo(() => {
      let data: TPeriodDataType = {};
      let lastYear = 0;

      [...(entries || [])]
        .filter((x) => x.paid_at || x.first_financial_at)
        .sort((a, b) => new Date(getHistoryEntryPaidDate(a)).getTime() - new Date(getHistoryEntryPaidDate(b)).getTime())
        ?.forEach((entry) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { category_id, sum } = entry;
          const paidAtOrFirstFinancialAt = getHistoryEntryPaidDate(entry);
          const period = paidAtOrFirstFinancialAt.slice(0, 7) || "";
          const [year] = period?.split("-").map(Number) || [];

          if (data[period] === undefined) data[period] = {};
          data[period][category_id] = (data[period][category_id] || 0) + Number(sum);

          if (lastYear && year > lastYear) {
            // new year found
            data[lastYear] = {};
            // assigning to any category since it doesnt matter, whole height will be color gray and height will be of highest month sum
            data[lastYear][1] =
              Math.max(
                ...Object.keys(data)
                  .filter((k) => k.startsWith(`${String(lastYear)}-`))
                  .map((key) => {
                    if (!data[key]) return 0;
                    return Object.values(data[key]).reduce((acc, cur) => acc + cur, 0);
                  })
              ) || 0;
          }

          lastYear = year;
        });

      return data;
    }, [entries]);

    const getPeriodTotalSum = useCallback(
      (prd: string) => {
        if (!chartData[prd]) return 0;
        return Object.values(chartData[prd]).reduce((acc, cur) => acc + cur, 0);
      },
      [chartData]
    );

    const handlePeriodClick = (period: string) => {
      if (selectedPeriod === period) {
        return;
      }

      setSelectedPeriod(period);
      onPeriodClick?.(period);
    };

    const previousMonthSum = useMemo(() => {
      // add .filter(([key]) => key.match(/^\d{4}-\d{2}$/gi)) in case you want to filter yyyy-MM, and exclude keys with only yyyy
      const sorted = Object.entries(chartData || {}).sort(
        (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()
      );
      const current = sorted.findIndex((x) => x[0] === selectedPeriod);

      if (current >= sorted.length - 1) return null;
      const prevPeriod = sorted[current + 1];
      return getPeriodTotalSum(prevPeriod[0]);
    }, [chartData, selectedPeriod, getPeriodTotalSum]);

    const highestSum = useMemo(() => {
      let highest = 0;

      Object.keys(chartData).forEach((key) => {
        const temp = getPeriodTotalSum(key);
        if (temp >= highest) {
          highest = temp;
        }
      });

      return highest;
    }, [chartData, getPeriodTotalSum]);

    const totalSelectedMonthSum = useMemo(() => {
      return getPeriodTotalSum(selectedPeriod);
    }, [selectedPeriod, getPeriodTotalSum]);

    const difference = ((totalSelectedMonthSum - (previousMonthSum || 1)) / (previousMonthSum || 1)) * 100;

    const sortChartDataKeys = (a: [string, Record<number, number>], b: [string, Record<number, number>]) => {
      const [aYear, aMonth] = a[0].split("-");
      const [bYear, bMonth] = b[0].split("-");

      if (!aMonth && aYear === bYear) return 1;
      if (!bMonth && bYear === aYear) return -1;
      return new Date(a[0]).getTime() - new Date(b[0]).getTime();
    };

    const formatPercentage = () => {
      const val = difference.toFixed(2);
      return val.endsWith(".00") ? val.slice(0, val.length - 3) : val;
    };

    const sliceAroundActivePeriod = useMemo(() => {
      const sorted = Object.entries(chartData).sort(sortChartDataKeys);
      if (!sorted.length)
        return {
          fromIndex: 0,
          toIndex: 0,
          selectedIndex: 0,
        };

      const currentIndex = sorted.findIndex((i) => i[0] === selectedPeriod);
      const { current: previous } = previousSelection;

      let fromIndex: number | undefined = previous?.fromIndex;
      let toIndex: number | undefined = previous?.toIndex;
      let calculatedFrom = currentIndex - barsToShow / 2;
      let calculatedTo = currentIndex + barsToShow / 2;
      // pressed side bars
      if (!!previous && currentIndex >= 0) {
        if (
          (currentIndex >= previous.toIndex - 1 && currentIndex < sorted.length) ||
          currentIndex <= previous.fromIndex
        ) {
          if (calculatedTo >= sorted.length) {
            calculatedFrom -= calculatedTo - sorted.length;
          }

          if (calculatedFrom <= 0) {
            calculatedTo += Math.abs(calculatedFrom);
          }

          fromIndex = Math.max(calculatedFrom, 0);
          toIndex = Math.min(calculatedTo, sorted.length);
        }
      }

      if (Number.isNaN(Number(fromIndex)) || Number.isNaN(Number(toIndex))) {
        if (calculatedTo >= sorted.length) {
          calculatedFrom -= calculatedTo - sorted.length;
        }

        if (calculatedFrom <= 0) {
          calculatedFrom = 0;
          calculatedTo = calculatedFrom + 6 <= sorted.length ? calculatedFrom + 6 : sorted.length;
        }

        fromIndex = calculatedFrom;
        toIndex = calculatedTo;
      }

      // update previous and return it
      return (previousSelection.current = {
        fromIndex,
        toIndex,
        selectedIndex: currentIndex,
      });
    }, [selectedPeriod, chartData, barsToShow]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "12rem",
          }}
        >
          {Object.entries(chartData || {})
            .sort(sortChartDataKeys)
            .slice(sliceAroundActivePeriod.fromIndex, sliceAroundActivePeriod.toIndex)
            .map(([period, values]) => {
              let totalSum = getPeriodTotalSum(period);
              const barHeight = (100 / highestSum) * totalSum;
              const [year, month] = period.split("-").map(Number);

              return (
                <Box
                  key={period}
                  sx={{
                    [theme.breakpoints.up("md")]: {
                      "&:not(:first-child)": {
                        marginLeft: "1rem",
                      },
                    },
                    height: "100%",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: "0.5",
                    minWidth: "2.5rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "fit-content",
                    cursor: "pointer",
                    justifyContent: "flex-end",
                    WebkitTapHighlightColor: "transparent",
                    ...(selectedPeriod === period && {
                      opacity: 1,
                    }),
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                  onClick={() => handlePeriodClick(period)}
                >
                  <Box
                    sx={{
                      width: "1.5rem",
                      marginBottom: "0.5rem",
                      minHeight: "1px",
                      animation: `${scaleY} 0.5s ease-in-out forwards`,
                      animationFillMode: "both",
                      transformOrigin: "bottom",
                      display: "flex",
                      flexDirection: "column",
                      height: `${barHeight}%`,
                    }}
                  >
                    {Object.entries(values || {}).map(([categoryId, categorySum]) => {
                      const percentValue = (100 / totalSum) * categorySum;

                      return (
                        <div
                          key={`${period}_${categoryId}_${categorySum}`}
                          style={{
                            backgroundColor: !month ? theme.palette.geyserGray.main : getColor(Number(categoryId)),
                            height: `${percentValue}%`,
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Typography component="span" sx={{ fontWeight: "normal", textTransform: "capitalize" }}>
                    {!month ? year : t(`months_short.${padMonth(Number(month))}`)}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", marginTop: "1.5rem" }}>
          <Typography variant="h5" component="span">
            {!selectedMonth ? t("year_review") : `${totalSelectedMonthSum?.toFixed(2)} €`}
          </Typography>
          {!selectedMonth && (
            <Typography component="span">{t("payments.total") + ` ${totalPeriodSum?.toFixed(2)} €`}</Typography>
          )}
          <Typography sx={{ textTransform: "capitalize", display: "flex", alignItems: "center" }}>
            {!selectedMonth ? selectedYear : t(`months.${padMonth(selectedMonth)}`)}
            {previousMonthSum ? `, ${formatPercentage()}%` : null}
            {previousMonthSum ? difference > 0 ? <Arrow up /> : <Arrow /> : null}
          </Typography>
        </Box>
      </Box>
    );
  }
);
HistoryBarChart.displayName = "HistoryBarChart";
